/**
 * Chat API - real-time chat rooms and messages.
 */
import apiClient, { apiGet, apiPost, apiPut, apiGetList } from "./client";
import type { PaginatedResponse } from "@/types";

// ─── Types ───────────────────────────────────────────────────────────

export interface ChatMember {
  id: string;
  user_id?: string;
  patient_id?: string;
  role: string;
  joined_at: string;
  last_read_at?: string;
}

export interface ChatRoom {
  id: string;
  organization_id: string;
  room_type: string;
  patient_id?: string;
  name?: string;
  members: ChatMember[];
  created_at: string;
  unread_count: number;
  latest_message_at?: string;
  latest_sender_id?: string;
}

export interface ChatMessage {
  id: string;
  organization_id: string;
  sender_id: string;
  sender_type: string;
  subject: string;
  body: string;
  channel: string;
  room_id?: string;
  status: string;
  sent_at?: string;
  created_at: string;
  attachments: ChatAttachment[];
}

export interface ChatAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  created_at: string;
}

export interface StaffPresence {
  user_id: string;
  online: boolean;
  last_seen_at?: string;
}

// ─── API Functions ───────────────────────────────────────────────────

/** Create a new chat room. */
export function createRoom(data: {
  room_type: string;
  name?: string;
  patient_id?: string;
  member_ids: string[];
}): Promise<ChatRoom> {
  return apiPost<ChatRoom>("/chat/rooms", data);
}

/** List all chat rooms for the current user. */
export function listRooms(): Promise<ChatRoom[]> {
  return apiGet<ChatRoom[]>("/chat/rooms");
}

/** Get a single chat room by ID. */
export function getRoom(id: string): Promise<ChatRoom> {
  return apiGet<ChatRoom>(`/chat/rooms/${id}`);
}

/** Send a message in a chat room. */
export function sendChatMessage(
  roomId: string,
  body: string,
): Promise<ChatMessage> {
  return apiPost<ChatMessage>(`/chat/rooms/${roomId}/messages`, { body });
}

/** List messages in a chat room (paginated, newest first). */
export function listRoomMessages(
  roomId: string,
  params?: { page?: number; per_page?: number },
): Promise<PaginatedResponse<ChatMessage>> {
  return apiGetList<ChatMessage>(`/chat/rooms/${roomId}/messages`, { params });
}

export function markRoomRead(
  roomId: string,
): Promise<{ room_id: string; read_at: string }> {
  return apiPut(`/chat/rooms/${roomId}/read`, {});
}

export async function sendChatMessageWithAttachments(
  roomId: string,
  body: string,
  files: File[],
  onProgress?: (percent: number) => void,
): Promise<ChatMessage> {
  const data = new FormData();
  data.append("body", body);
  files.forEach((file) => data.append("files", file));
  const response = await apiClient.post<{ data: ChatMessage }>(
    `/chat/rooms/${roomId}/messages/attachments`,
    data,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (event.total) {
          onProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      },
    },
  );
  return response.data.data;
}

export async function downloadChatAttachment(id: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(`/chat/attachments/${id}/file`, {
    responseType: "blob",
  });
  return response.data;
}

/** List staff online / last-seen presence for the current organization. */
export async function listStaffPresence(): Promise<StaffPresence[]> {
  const response = await apiGet<{ users: StaffPresence[] }>("/chat/presence");
  return response.users ?? [];
}
